// utils/videoUtils.js
// Created: 2026-03-11 14:30:00 EST
// Updated: 2026-03-11 14:30:00 EST
// Project: Giving Words to Voices
// Author: Michael Schwarz
// Version: 1.0.0

import { VIDEO_SERVER_URL } from '../constants';

/**
 * Encode a server-relative path for use in a <source src> or fetch URL.
 * Each path segment is encoded individually so slashes are preserved.
 * Handles spaces, brackets, parentheses, and other special chars that
 * break unencoded URLs
 * e.g. "[ SHOAH ]/Clari Rosenblum (Samuel)/file.mp4"
 *
 * @param {string} subdir  - relative subdirectory path (may be empty)
 * @param {string} filename - the file name
 * @returns {string} fully encoded URL
 */
export function encodeVideoPath(subdir, filename) {
  const segments = [...(subdir ? subdir.split('/') : []), filename]
    .filter(Boolean)
    .map(s => encodeURIComponent(s));
  return `${VIDEO_SERVER_URL}/${segments.join('/')}`;
}

/**
 * Build a deduplicated list of MP4 stem candidates for a given VTT video_id.
 * Handles both dot-lang suffixes  ("name.pt1.sv" → "name - pt1")
 * and underscore-lang suffixes    ("name_Tape1_sv" → "name_Tape1").
 * Returns stems in priority order; caller appends ".mp4".
 *
 * @param {string} s - video_id stem (without .vtt extension)
 * @returns {string[]} deduplicated list of candidate stems
 */
export function buildMp4Candidates(s) {
  const stripDotLang   = () => s.replace(/\.[a-z]{2}$/i, '');
  const stripUnderLang = () => s.replace(/_[a-z]{2}$/i, '');
  const convertPart    = (stem) =>
    stem.replace(/\.(pt\d+)$/i, ' - $1').replace(/_(pt\d+)$/i, ' - $1');

  const dotLangStripped = stripDotLang();
  const underLangStripped = stripUnderLang();

  return [...new Set([
    convertPart(dotLangStripped),   // dot-lang stripped + part converted          ← legacy files
    dotLangStripped,                // dot-lang stripped only
    convertPart(underLangStripped), // underscore-lang stripped + part converted   ← SHOAH files
    underLangStripped,              // underscore-lang stripped only
    s,                             // as-is fallback
  ])];
}

/**
 * Derive the project root from a video_id — mirrors backend derive_project_root().
 * Strips trailing part/tape/language suffixes to get the canonical subject name.
 *
 * e.g. "Chaim David Szpigler.pt1.sv" → "Chaim David Szpigler"
 *      "Klari_Rosenblum_Tape1_sv"    → "Klari_Rosenblum"
 *
 * @param {string} videoId
 * @returns {string}
 */
export function getProjectRoot(videoId) {
  let root = videoId;
  // Strip tape/part/episode label + number + optional lang suffix
  root = root.replace(
    /[._\s-]*(tape|part|pt|episode|ep)[._\s-]*\d+[._\s-]*(sv|en|he|pl|de|fr|ru|yi|yid|ar)?$/i,
    ''
  );
  // Strip bare trailing number + optional lang suffix
  root = root.replace(/[._\s-]*\d+[._\s-]*(sv|en|he|pl|de|fr|ru|yi|yid|ar)?$/i, '');
  // Strip lone lang suffix leftover
  root = root.replace(/[._\s-]*(sv|en|he|pl|de|fr|ru|yi|yid|ar)$/i, '');
  // Strip trailing separators
  root = root.replace(/[._\s-]+$/, '');
  return root.trim();
}
